import { Modal } from '@bfe/moly';
import { useFm, useScreenType } from '@web3/base-hooks';
import { justFormatNumber } from '@web3/base-utils';
import dayjs from 'dayjs';
import { formatTimeDiff } from '@/utils/day';
import { maskAddress } from '@/utils';
import clsx from 'clsx';
import { Report } from '@/types/detailAll';
import { getReportStatusColor, getReportStatusText } from '../../utils';

interface DetailModalProps {
  entity: string;
  visible: boolean;
  onClose: () => void;
  data: Report;
}

export const DetailModal = ({
  visible,
  onClose,
  data,
  entity,
}: DetailModalProps) => {
  const fm = useFm();
  const { isMobile } = useScreenType();

  const modalH5Props = {
    isMobile: true,
    direction: 'bottom',
    contextWrapper: '!px-4',
    headerWrapper: '!px-0 detail-modal-header-wrapper',
  };

  return (
    <Modal
      title={fm('viewDetail')}
      onCancel={onClose}
      open={visible}
      footer={null}
      {...(isMobile ? modalH5Props : {})}
    >
      <div>
        <div className="bg-bds-gray-ele-line p-3 rounded-lg">
          <span
            style={{
              backgroundColor: getReportStatusColor(data?.status).bg,
              color: getReportStatusColor(data?.status).text,
            }}
            className="px-2 py-1 rounded-md"
          >
            {getReportStatusText(data?.status)
              ? fm(getReportStatusText(data?.status))
              : ''}
          </span>
          <ul className="flex flex-col gap-2 mt-[10px] md:gap-4">
            <li className="flex justify-between items-center">
              <span className="text-bds-gray-t2">{fm('fundRecipient')}</span>
              <span>{entity}</span>
            </li>
            <li className="flex justify-between items-center">
              <span className="text-bds-gray-t2">{fm('reportedFunds')}</span>
              <span className="font-semibold">
                {data?.fundAmount
                  ? `$${justFormatNumber(data?.fundAmount)}`
                  : '--'}
              </span>
            </li>
            {!isMobile && (
              <>
                <li className="flex justify-between items-center">
                  <span className="text-bds-gray-t2">{fm('blockedFunds')}</span>
                  <span className="font-semibold">
                    {data?.blockedFunds
                      ? `$${justFormatNumber(data?.blockedFunds)}`
                      : '--'}
                  </span>
                </li>
                <li className="flex justify-between items-center">
                  <span className="text-bds-gray-t2">{fm('lostFunds')}</span>
                  <span className="font-semibold">
                    {data?.lostFunds
                      ? `$${justFormatNumber(data?.lostFunds)}`
                      : '--'}
                  </span>
                </li>
                <li className="flex justify-between items-center">
                  <span className="text-bds-gray-t2">
                    {fm('outflowedFunds')}
                  </span>
                  <span className="font-semibold">
                    {data?.backOnChainFunds
                      ? `$${justFormatNumber(data?.backOnChainFunds)}`
                      : '--'}
                  </span>
                </li>
              </>
            )}
            <li className="flex justify-between items-center">
              <span className="text-bds-gray-t2">{fm('responseTime')}</span>
              <span className="font-semibold">
                {formatTimeDiff(
                  (data?.entityResponseTime ?? Date.now()) -
                    data?.contactEntityTime
                )}
              </span>
            </li>
            <li className="flex justify-between items-center">
              <span className="text-bds-gray-t2">{fm('recipientAddress')}</span>
              <span>
                {/* 不暴露用户信息，后端会做脱敏省略 */}
                {data?.entityUserAddress || '--'}
                {/* {data?.entityUserAddress
                  ? maskAddress({
                      address: data?.entityUserAddress,
                      before: 10,
                      after: 4,
                    })
                  : '--'} */}
              </span>
            </li>
          </ul>
        </div>
        <div
          className={clsx('text-bds-gray-t1-title font-medium md:py-6 py-4')}
        >
          {fm('reporterInfo')}
        </div>
        <ul className="flex flex-col gap-3 md:gap-4">
          <li className="flex justify-between items-center">
            <span className="text-bds-gray-t2">{fm('contributors3')}</span>
            <span>
              {/* 不暴露用户信息，后端会做脱敏省略 */}
              {data?.reporterName || '--'}
              {/* {data?.reporterName
                ? data.reporterName?.length > 10
                  ? maskAddress({
                      address: data.reporterName,
                      before: 10,
                      after: 4,
                    })
                  : data.reporterName
                : '--'} */}
            </span>
          </li>
          <li className="flex justify-between items-center">
            <span className="text-bds-gray-t2">{fm('estBounty')}</span>
            <span>
              {data?.estBounty ? `$${justFormatNumber(data?.estBounty)}` : '--'}
            </span>
          </li>
          <li className="flex justify-between">
            <span className="text-bds-gray-t2 ">{fm('relatedTxHash')}</span>
            <span className="w-40 text-right break-all">
              {data?.txHash || '--'}
            </span>
          </li>
          <li className="flex justify-between">
            <span className="text-bds-gray-t2">{fm('reportFile')}</span>
            <span className="w-40 text-right break-all">
              {data?.reportFile || '--'}
            </span>
          </li>
          <li className="flex justify-between">
            <span className="text-bds-gray-t2">{fm('reportText')}</span>
            <span className="w-40 text-right break-all">
              {data?.reportText || '--'}
            </span>
          </li>
          <li className="flex justify-between items-center">
            <span className="text-bds-gray-t2">{fm('reportTime')}</span>
            <span>{dayjs(data?.reportTime).format('YYYY-MM-DD HH:mm:ss')}</span>
          </li>
        </ul>
      </div>
    </Modal>
  );
};
